import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormMessage,
} from '@/components/ui/form';
import {
	InputOTP,
	InputOTPGroup,
	InputOTPSlot,
} from '@/components/ui/input-otp';
import OwnInnIcon from '@/components/icons/OwnInnIcon';
import { useTranslation } from 'react-i18next';
import { t } from 'i18next';
import { useTwoFactorLoginApi } from '@/features/identity/hooks/useTwoFactorLoginApi.ts';
import { useLocation, useNavigate } from 'react-router-dom';
import { ErrorKey, isUnavailableAuthError } from '@/config/apiErrorKeys.ts';
import EToast from '@/components/everridge/EToast.tsx';
import { useAuthForm } from '@/features/identity/hooks/useAuthForm.ts';
import { LoginResponseDto } from '@/@types/auth.types.ts';
import { AuthHelper } from '@/utils/auth.helper.ts';
import { useSignIn } from '@/features/identity/hooks/useSignIn.ts';
import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { healthCheckQueryKey } from '@/features/maintenance/healthCheckQuery';
import { setMaintenanceRedirectState } from '@/features/maintenance/redirectState.ts';

const FormSchema = z.object({
	pin: z.string().min(4, {
		message: t('auth.verifyEmailForm.form.pin.error'),
	}),
});

const TwoFactorLoginForm = () => {
	const { doTwoFactorLogin, isDoTwoFactorLogin } = useTwoFactorLoginApi({
		onSuccess,
		onError,
	});
	const location = useLocation();
	const navigate = useNavigate();
	const { t } = useTranslation();
	const { email, twoFactorToken } = useAuthForm();
	const signIn = useSignIn();
	const queryClient = useQueryClient();
	const returnUrl = new URLSearchParams(location.search).get('returnUrl');

	const getHiddenEmail = useCallback((email?: string) => {
		if (!email) return null;
		const [userName, domain] = email.split('@');
		const hiddenUserName = `${userName[0]}***${
			userName[userName.length - 1]
		}`;
		return `${hiddenUserName}@${domain}`;
	}, []);

	function onSuccess(result: LoginResponseDto) {
		const claims = AuthHelper.parseJwtTokenClaims(
			result.tokens.accessToken,
		);
		const user = AuthHelper.createUser(result.user, claims);

		if (!user) {
			EToast({
				variant: 'error',
				message: t('auth.loginForm.error.message'),
				description: t('auth.loginForm.error.description'),
			});
			return;
		}

		signIn({
			tokens: result.tokens,
			user,
		});

		navigate('/auth/choose-hotel', { replace: true });
	}

	function onError(error: ErrorKey) {
		if (isUnavailableAuthError(error)) {
			queryClient.invalidateQueries({ queryKey: healthCheckQueryKey });
			setMaintenanceRedirectState(returnUrl);
			EToast({
				message: t('auth.unavailable.message'),
				description: t('auth.unavailable.description'),
				variant: 'error',
			});
			navigate('/maintenance', { replace: true });
			return;
		}

		EToast({
			message: t('auth.loginForm.error.message'),
			description: t('auth.loginForm.error.description'),
			variant: 'error',
		});
	}

	const form = useForm<z.infer<typeof FormSchema>>({
		resolver: zodResolver(FormSchema),
	});

	async function onSubmit(data: z.infer<typeof FormSchema>) {
		if (!twoFactorToken) {
			EToast({
				variant: 'error',
				message: t('auth.twoFactorLoginForm.error.noToken.title'),
				description: t(
					'auth.twoFactorLoginForm.error.noToken.description',
				),
			});
			return;
		}

		await doTwoFactorLogin({
			code: data.pin,
			token: twoFactorToken!,
		});
	}

	if (!email || !twoFactorToken) {
		return (
			<div className='flex items-center justify-center h-full'>
				<p className='text-popover-800'>
					{t('auth.twoFactorLoginForm.error.noEmailOrToken')}
				</p>
			</div>
		);
	}

	return (
		<Form {...form}>
			<div className='px-8 py-14 flex flex-col gap-6 rounded-2.5xl bg-popover-10'>
				<div className='flex flex-col items-center gap-4'>
					<OwnInnIcon className='size-11' />
					<div className='flex flex-col items-center gap-2'>
						<h3 className='text-2xl font-semibold select-none'>
							{t('auth.verifyEmailForm.text.title')}
						</h3>
						<p className='text-sm text-popover-800 font-medium'>
							{t('auth.verifyEmailForm.text.subtitle')}
							<span className='text-popover-1000'>
								{getHiddenEmail(email)}
							</span>
						</p>
					</div>
				</div>
				<form
					className='w-[20.5rem] flex flex-col gap-8'
					onSubmit={form.handleSubmit(onSubmit)}>
					<FormField
						control={form.control}
						name='pin'
						render={({ field }) => (
							<FormItem>
								<FormControl>
									<InputOTP
										maxLength={4}
										{...field}>
										<InputOTPGroup>
											<InputOTPSlot index={0} />
										</InputOTPGroup>
										<InputOTPGroup>
											<InputOTPSlot index={1} />
										</InputOTPGroup>
										<InputOTPGroup>
											<InputOTPSlot index={2} />
										</InputOTPGroup>
										<InputOTPGroup>
											<InputOTPSlot index={3} />
										</InputOTPGroup>
									</InputOTP>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
					<div className='flex flex-col gap-4'>
						<Button type='submit'>
							{t('elements.buttons.verify')}
						</Button>
						<Button
							onClick={() => navigate('/auth/login')}
							type='button'
							variant='link'
							className='p-0 hover:text-popover-800 active:text-popover-600'>
							{t('elements.buttons.backToLogin')}
						</Button>
					</div>
				</form>
			</div>
		</Form>
	);
};

export default TwoFactorLoginForm;
